import React, { useCallback, useContext, useState, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { SearchMenuBar, type SearchMenuItem, UserContext } from 'venn-components';
import { isRequestSuccessful, useHasFF, withSuspense } from 'venn-utils';
import { createScenario, getDefaultIndicesForUser, type ScenarioAnalysisIndex } from 'venn-api';
import { ScenarioDropdown, ScenarioDropdownShimmer } from './ScenarioDropdown';
import { useScenarioRecoilState } from './useScenarioRecoilState';
import { groupBy, isEmpty, sortBy } from 'lodash';
import toPairs from 'lodash/toPairs';
import { AccordionMenu, getAppTitle, GetColor, Icon, SimpleTrigger, SkeletalDropMenu, Tooltip } from 'venn-ui-kit';
import styled from 'styled-components';

const isMarket = (scenario: ScenarioAnalysisIndex) => scenario.type === 'MARKET' && !scenario.category;
const isMacro = (scenario: ScenarioAnalysisIndex) => scenario.type === 'MACRO' && !scenario.category;
const hasAssignedCategory = (scenario: ScenarioAnalysisIndex) => !!scenario.category;

const CATEGORIES_PRIORITY = {
  'Broad Equities': 0,
  'Broad Fixed Income': 1,
  'US Equity Style Box': 2,
  'Equity Countries': 3,
  'Global Equity Sectors': 4,
  Commodities: 5,
  Currencies: 6,
  Inflation: 7,
  Volatility: 8,
  Infrastructure: 9,
};

const toOption = (scenario: ScenarioAnalysisIndex) => ({
  label: scenario.fundName,
  value: scenario,
});

const SuspenseState = () => (
  <>
    {useHasFF('macro_scenario_ff') && <ScenarioDropdownShimmer />}
    <ScenarioDropdownShimmer />
  </>
);

export const ScenarioDropdowns = withSuspense(<SuspenseState />, ({ selected }: { selected: string }) => {
  const hasImprovedScenarioSelection = useHasFF('scenario_hack_day_ff');
  const showMacroScenarios = useHasFF('macro_scenario_ff');

  const { hasPermission } = useContext(UserContext);
  const isReadOnly = !hasPermission('STUDIO_EDIT_METRICS');
  const { addScenario, addCustomScenario, addCategorizedScenario } = useScenarioRecoilState(selected);

  const { data: scenarios = [] } = useQuery<ScenarioAnalysisIndex[]>(
    [],
    async () => (await getDefaultIndicesForUser()).content,
    {
      suspense: true,
    },
  );

  const marketItems = scenarios.filter(isMarket).map(toOption);
  const macroItems = scenarios.filter(isMacro).map(toOption);

  const categorizedItems = scenarios.filter(hasAssignedCategory);
  const allCategories = sortBy(
    toPairs(groupBy(categorizedItems, 'category')).map((pair) => {
      const [categoryName, entries] = pair;
      return {
        label: categoryName,
        value: categoryName,
        items: entries.map(toOption),
        priority: CATEGORIES_PRIORITY[categoryName] ?? Number.MAX_VALUE,
      };
    }),
    'priority',
  );

  const createCustomScenarioConfig = useCallback(
    async (item: SearchMenuItem) => {
      if (!item.value || !item.value?.id?.toString()) {
        return;
      }
      try {
        const response = await createScenario({
          fundId: item.value.id.toString(),
          fundName: item.label,
          type: 'MARKET',
          units: 'PERCENTAGE',
          active: true,
          seriesType: 'RETURN',
        });

        if (isRequestSuccessful(response)) {
          addCustomScenario(response.content);
        } else {
          // eslint-disable-next-line no-console
          console.log('Some error occurred'); // TODO (hack-day): change this
          // eslint-disable-next-line no-console
          console.log(response);
        }
      } catch (error) {
        // logExceptionIntoSentry(error); // TODO (hack-day): change this
        // eslint-disable-next-line no-console
        console.log(error);
      }
    },
    [addCustomScenario],
  );

  const onSelect = useCallback(
    (item: ScenarioAnalysisIndex | string) => {
      if (typeof item === 'string') {
        return;
      }

      addCategorizedScenario(item);
    },
    [addCategorizedScenario],
  );

  const [search, setSearch] = useState('');
  const filteredItems = useMemo(() => {
    if (search.length === 0) {
      return allCategories;
    }
    const filteredCategories = [];
    // Simple quick search
    for (const category of allCategories) {
      if (category.label.toLowerCase().includes(search.toLowerCase())) {
        filteredCategories.push(category);
      } else if (category.items && category.items.length > 0) {
        const items = category.items.filter((i) => i.label.toLowerCase().includes(search.toLowerCase()));
        if (items.length > 0) {
          filteredCategories.push({ ...category, items });
        }
      }
    }
    return filteredCategories;
  }, [allCategories, search]);

  return (
    <>
      {showMacroScenarios && (
        <ScenarioDropdown
          label="Macroeconomic Indicator"
          placeholder="Add indicator..."
          onChange={addScenario}
          disabled={isReadOnly}
          items={macroItems}
        />
      )}
      {hasImprovedScenarioSelection ? (
        <Container>
          <SkeletalDropMenu
            menuPosition="left"
            usePortal
            triggerComponent={(expanded, toggleMenu) => (
              <SimpleTrigger
                label="Shock input"
                placeholder="Search recommended market shocks..."
                expanded={expanded}
                onClick={toggleMenu}
                aria-expanded={expanded}
                aria-haspopup
                searchable
                search={search}
                onSearch={(text) => {
                  setSearch(text);
                }}
              />
            )}
            menuComponent={() => (
              <AccordionContainer style={{}}>
                {isEmpty(filteredItems) ? (
                  <NoResultsContainer>No results found.</NoResultsContainer>
                ) : (
                  <AccordionMenu
                    expanded
                    idGetter={(value: ScenarioAnalysisIndex | string) =>
                      typeof value === 'string' ? value : value?.id
                    }
                    onSelect={onSelect}
                    items={filteredItems}
                  />
                )}
              </AccordionContainer>
            )}
          />
          <Tooltip
            usePortal
            content={
              <div>
                {`Subscribers are responsible for evaluating whether a custom entry is appropriate for sensitivity
                analysis. ${getAppTitle()} does not recommend the use of custom entries`}
              </div>
            }
            maxWidth={400}
          >
            or select custom input <WarningIcon type="triangle-exclamation" />
          </Tooltip>
          <SearchMenuBar
            onSelected={createCustomScenarioConfig}
            value={null}
            usePortal
            fixedMenuWidth
            location="scenario-picker"
            investmentsOnly
            showRecentlyAnalyzed={false}
          />
        </Container>
      ) : (
        <ScenarioDropdown
          label="Market Index"
          placeholder="Add index..."
          onChange={addScenario}
          disabled={isReadOnly}
          items={marketItems}
        />
      )}
    </>
  );
});

const Container = styled.div`
  display: flex;
  flex-direction: column;
  font-size: 12px;
  font-weight: bold;
  padding-bottom: 10px;
  border-bottom: 1px solid ${GetColor.Grey};
  margin-bottom: 10px;
  gap: 6px;
`;

const AccordionContainer = styled.div`
  width: 500px;
  max-height: 650px;
  overflow-x: hidden;
  overflow-y: auto;
  font-weight: normal;
  background-color: ${GetColor.White};
  .accordion-content-wrapper-btn {
    width: 500px;
  }
`;

const NoResultsContainer = styled.div`
  font-weight: bold;
  padding: 10px;
`;

const WarningIcon = styled(Icon)`
  color: ${GetColor.Warning};
`;
